import React from "react";
import { Button, ListItem, Theme, WithStyles, createStyles, withStyles } from "@material-ui/core";
import { PlayCircleFilled } from "@material-ui/icons";
import Moment from "moment";
import { Game } from "../../types/game";
import RangeGameBadge from "./RangeGameBadge";
import { isRangeGame } from "../../utils/rhiGame";
import { ENABLE_RHI } from "../../config/featureFlags";
import { MOBILE_BREAKPOINT } from "../../consts/dialog";
import { GAME_MENU_HEADER_BORDER, LIVE_GAME_MENU_COLORS, RANGE_GAME_COLORS } from "./rangeGameStyles";
import GameCodeCapsule from "./GameCodeCapsule";

export type GameDialogHeaderProps = {
  game: Game;
  showPlay: boolean;
  playDisabled: boolean;
  onPlay: () => void;
};

type Props = GameDialogHeaderProps & WithStyles<typeof styles>;

const GameDialogHeader = ({
  game,
  showPlay,
  playDisabled,
  onPlay,
  classes,
}: Props) => {
  const rangeGame = isRangeGame(game);
  const date = Moment(game.date);

  return (
    <ListItem
      className={rangeGame ? classes.rootRange : classes.root}
      disableGutters
      classes={{ container: classes.listItemContainer }}
    >
      <div className={classes.grid}>
        <div className={classes.titleCol}>
          <div className={classes.titleBlock}>
            <span className={classes.title} title={game.name}>
              {game.name}
            </span>
            {ENABLE_RHI && rangeGame && <RangeGameBadge game={game} variant="menu" className={classes.rangeBadge} />}
          </div>
        </div>

        <div className={classes.playCol}>
          {showPlay && (
            <div className={classes.playWrap}>
              {!playDisabled && <span className={classes.playWave} aria-hidden="true" />}
              <Button
                disabled={playDisabled}
                color="primary"
                variant="contained"
                className={classes.playButton}
                onClick={onPlay}
              >
                <div className={classes.playButtonInner}>
                  <PlayCircleFilled className={classes.playIcon} />
                  <span className={classes.playLabel}>Play</span>
                </div>
              </Button>
            </div>
          )}
        </div>

        <div className={classes.metaCol}>
          <GameCodeCapsule code={game.code} className={classes.gameCodeSize} />
          <span className={classes.gameDate}>{date.format("ll")}</span>
        </div>
      </div>
    </ListItem>
  );
};

const headerPadding = (theme: Theme) => ({
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2.5),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
});

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...headerPadding(theme),
      backgroundColor: LIVE_GAME_MENU_COLORS.backgroundColor,
      backgroundImage: LIVE_GAME_MENU_COLORS.backgroundImage,
      borderBottom: GAME_MENU_HEADER_BORDER,
    },
    rootRange: {
      ...headerPadding(theme),
      backgroundColor: RANGE_GAME_COLORS.bg,
      borderBottom: GAME_MENU_HEADER_BORDER,
    },
    listItemContainer: {
      width: "100%",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
      alignItems: "center",
      width: "100%",
      columnGap: theme.spacing(2),
      minHeight: 76,
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        columnGap: theme.spacing(1.25),
        minHeight: 68,
      },
    },
    titleCol: {
      gridColumn: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      minWidth: 0,
      paddingRight: theme.spacing(0.5),
    },
    titleBlock: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      minWidth: 0,
      width: "100%",
      gap: theme.spacing(0.75),
    },
    rangeBadge: {
      marginLeft: -3,
    },
    title: {
      display: "-webkit-box",
      width: "100%",
      margin: 0,
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
      letterSpacing: "-0.015em",
      color: theme.palette.text.primary,
      minWidth: 0,
      overflow: "hidden",
      overflowWrap: "anywhere",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        fontSize: "0.9375rem",
        lineHeight: 1.35,
      },
      [`@media (max-width: 380px)`]: {
        fontSize: "0.8125rem",
        lineHeight: 1.3,
      },
    },
    playCol: {
      gridColumn: 2,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexShrink: 0,
    },
    playWrap: {
      position: "relative",
      width: 76,
      height: 76,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "visible",
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        width: 68,
        height: 68,
      },
    },
    playWave: {
      position: "absolute",
      inset: 0,
      borderRadius: "50%",
      border: "2px solid rgba(21, 101, 192, 0.38)",
      pointerEvents: "none",
      animation: "$playWave 4s cubic-bezier(0.23, 1, 0.32, 1) infinite",
      "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
        display: "none",
      },
    },
    "@keyframes playWave": {
      "0%": {
        transform: "scale(1)",
        opacity: 0.42,
      },
      "42%": {
        transform: "scale(1.36)",
        opacity: 0,
      },
      "100%": {
        transform: "scale(1.36)",
        opacity: 0,
      },
    },
    playButton: {
      width: 76,
      height: 76,
      minWidth: 76,
      borderRadius: "50%",
      zIndex: 1,
      overflow: "visible",
      boxShadow: "0 5px 16px rgba(21, 101, 192, 0.28)",
      transition: "transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        width: 68,
        height: 68,
        minWidth: 68,
      },
      "&:hover": {
        boxShadow: "0 7px 20px rgba(21, 101, 192, 0.32)",
      },
      "&:active": {
        transform: "scale(0.98)",
        boxShadow: "0 2px 8px rgba(21, 101, 192, 0.16)",
      },
    },
    playButtonInner: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1,
      gap: 3,
    },
    playIcon: {
      fontSize: 28,
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        fontSize: 24,
      },
    },
    playLabel: {
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      lineHeight: 1,
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        fontSize: "0.625rem",
      },
    },
    metaCol: {
      gridColumn: 3,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "center",
      textAlign: "right",
      minWidth: 0,
      flexShrink: 0,
      gap: theme.spacing(0.375),
      paddingLeft: theme.spacing(0.5),
    },
    gameCodeSize: {
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        fontSize: "0.8125rem",
      },
    },
    gameDate: {
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.3,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        fontSize: "0.6875rem",
      },
    },
  });

export default withStyles(styles)(GameDialogHeader);
